if (!customElements.get('announcement-bar-slider')) {
  class AnnouncementBarSlider extends HTMLElement {

    connectedCallback() {
      this._track = this.querySelector('.announcement-bar__track');
      if (!this._track) return;

      // Purge any clones left from a previous init (customizer in-place reload)
      this._track.querySelectorAll('[data-clone]').forEach((el) => el.remove());

      this._slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));

      // Nothing to do for 0 or 1 slide — leave DOM completely untouched
      if (this._slides.length <= 1) return;

      this._realCount = this._slides.length;
      this._rawIndex  = 1; // offset by 1 because we prepend a clone of the last slide
      this._autoplayTimer   = null;
      this._transitioning   = false;
      this._pointerStartX   = null;
      this._pointerStartY   = null;
      this._isDragging      = false;
      this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._autoplay  = this.dataset.autoplay === 'true';
      this._interval  = parseInt(this.dataset.interval, 10) || 5000;

      // Build clone bookends for seamless infinite loop:
      // track = [clone-of-last] [slide-1] … [slide-N] [clone-of-first]
      //           rawIndex 0       1            N          N+1
      const firstClone = this._slides[0].cloneNode(true);
      const lastClone  = this._slides[this._realCount - 1].cloneNode(true);
      firstClone.setAttribute('aria-hidden', 'true');
      firstClone.setAttribute('data-clone', 'true');
      lastClone.setAttribute('aria-hidden', 'true');
      lastClone.setAttribute('data-clone', 'true');
      this._track.appendChild(firstClone);
      this._track.insertBefore(lastClone, this._slides[0]);

      this._track.addEventListener('transitionend', () => this._onTransitionEnd());

      // Render initial position without animation
      this._moveTo(1, false);

      // Arrow buttons
      const prev = this.querySelector('[data-dir="prev"]');
      const next = this.querySelector('[data-dir="next"]');
      if (prev) prev.addEventListener('click', () => { if (!this._transitioning) this._advance(-1); });
      if (next) next.addEventListener('click', () => { if (!this._transitioning) this._advance(1); });

      // Swipe
      this._bindSwipe();

      // Resize observer for text-wrap detection
      this._resizeObserver = new ResizeObserver(() => this._checkTextWrap());
      this._resizeObserver.observe(this);

      // Shopify customizer events
      this._bindEditorEvents();

      // Autoplay
      if (this._autoplay && !this._prefersReducedMotion) {
        this._startAutoplay();
        this.addEventListener('mouseenter',  () => this._stopAutoplay());
        this.addEventListener('mouseleave',  () => this._startAutoplay());
        this.addEventListener('focusin',     () => this._stopAutoplay());
        this.addEventListener('focusout',    () => this._startAutoplay());
        document.addEventListener('visibilitychange', () => {
          document.hidden ? this._stopAutoplay() : this._startAutoplay();
        });
      }
    }

    disconnectedCallback() {
      if (this._autoplayTimer) this._stopAutoplay();
      if (this._onSectionLoad) this._unbindEditorEvents();
      if (this._resizeObserver) this._resizeObserver.disconnect();
    }

    _advance(dir) {
      this._rawIndex += dir;
      this._moveTo(this._rawIndex);
    }

    _moveTo(rawIndex, animate = true) {
      this._rawIndex = rawIndex;

      if (!animate || this._prefersReducedMotion) {
        this._track.style.transition = 'none';
        this._transitioning = false;
      } else {
        this._track.style.transition = '';
        this._transitioning = true;
      }

      this._track.style.transform = `translateX(-${rawIndex * 100}%)`;
      this._updateAria();
      this._syncHostColors();
      this._checkTextWrap();
      this._notifyHeaderResize();
    }

    _onTransitionEnd() {
      this._transitioning = false;
      if (this._rawIndex === this._realCount + 1) {
        this._moveTo(1, false);
      } else if (this._rawIndex === 0) {
        this._moveTo(this._realCount, false);
      }
    }

    get _realIndex() {
      return ((this._rawIndex - 1) % this._realCount + this._realCount) % this._realCount;
    }

    _updateAria() {
      const active = this._realIndex;
      this._slides.forEach((slide, i) => {
        const isActive = i === active;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        const link = slide.querySelector('.announcement-bar__overlay-link');
        if (link) link.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    _syncHostColors() {
      const slide = this._slides[this._realIndex];
      if (!slide) return;
      const bg   = slide.style.getPropertyValue('--ab-bg').trim();
      const text = slide.style.getPropertyValue('--ab-text').trim();
      if (bg)   this.style.setProperty('--ab-current-bg',   bg);
      if (text) this.style.setProperty('--ab-current-text', text);
    }

    _checkTextWrap() {
      this._slides.forEach((slide) => {
        const el = slide.querySelector('.announcement-bar__text');
        if (!el) return;
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
        slide.classList.toggle('announcement-bar__slide--multiline', el.offsetHeight > lineHeight);
      });
    }

    _notifyHeaderResize() {
      const header = document.querySelector('theme-header');
      if (header && typeof header.recalculateAll === 'function') {
        requestAnimationFrame(() => header.recalculateAll());
      }
    }

    _startAutoplay() {
      this._stopAutoplay();
      if (!this._autoplay || this._prefersReducedMotion) return;
      this._autoplayTimer = setInterval(() => {
        if (!this._transitioning) this._advance(1);
      }, this._interval);
    }

    _stopAutoplay() {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }

    _bindEditorEvents() {
      const sectionId = () =>
        this.closest('[data-shopify-section-id]')?.dataset.shopifySectionId;

      this._onSectionLoad = (e) => {
        if (e.detail.sectionId !== sectionId()) return;
        this._stopAutoplay();
        this._moveTo(this._rawIndex, false);
        if (this._autoplay) this._startAutoplay();
      };

      this._onSectionUnload = (e) => {
        if (e.detail.sectionId !== sectionId()) return;
        this._stopAutoplay();
      };

      this._onBlockSelect = (e) => {
        const idx = this._slides.findIndex((s) => s.dataset.shopifyBlockId === e.detail.blockId);
        if (idx === -1) return;
        this._stopAutoplay();
        this._moveTo(idx + 1); // +1 for the prepended clone
      };

      this._onBlockDeselect = () => {
        if (this._autoplay) this._startAutoplay();
      };

      document.addEventListener('shopify:section:load',    this._onSectionLoad);
      document.addEventListener('shopify:section:unload',  this._onSectionUnload);
      document.addEventListener('shopify:block:select',    this._onBlockSelect);
      document.addEventListener('shopify:block:deselect',  this._onBlockDeselect);
    }

    _unbindEditorEvents() {
      document.removeEventListener('shopify:section:load',    this._onSectionLoad);
      document.removeEventListener('shopify:section:unload',  this._onSectionUnload);
      document.removeEventListener('shopify:block:select',    this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect',  this._onBlockDeselect);
    }

    _bindSwipe() {
      this.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || this._transitioning) return;
        this._pointerStartX = e.clientX;
        this._pointerStartY = e.clientY;
        this._isDragging = false;
      });
      this.addEventListener('pointermove', (e) => {
        if (this._pointerStartX === null) return;
        if (Math.abs(e.clientX - this._pointerStartX) > 5 ||
            Math.abs(e.clientY - this._pointerStartY) > 5) {
          this._isDragging = true;
        }
      });
      this.addEventListener('pointerup', (e) => {
        if (this._pointerStartX === null) return;
        const dx = e.clientX - this._pointerStartX;
        if (Math.abs(dx) >= 40 && this._isDragging) {
          this._stopAutoplay();
          this._advance(dx < 0 ? 1 : -1);
          if (this._autoplay) this._startAutoplay();
        }
        this._pointerStartX = null;
        this._pointerStartY = null;
        this._isDragging = false;
      });
      this.addEventListener('pointercancel', () => {
        this._pointerStartX = null;
        this._pointerStartY = null;
        this._isDragging = false;
      });
    }
  }

  customElements.define('announcement-bar-slider', AnnouncementBarSlider);
}
